import { type CSSResultGroup, LitElement, css, html, nothing } from "lit";
import { property } from "lit/decorators.js";
import "@whilecat/core/shoe.ts";

export interface Params {
    visible?: boolean;
    disabled?: boolean;
    toggled?: boolean;
}

export abstract class BaseExtraButton extends LitElement {
    @property({ type: Boolean }) visible = true;
    @property({ type: Boolean }) disabled = false;
    @property({ type: Boolean }) toggled = false;
    @property() readonly tooltip: string;
    @property() readonly tooltipToggled: string;
    @property() readonly iconName: string;
    @property() readonly toggleIconName: string;

    constructor(iconName: string, toggleIconName: string, tooltip: string, tooltipToggled: string = tooltip) {
        super();
        this.iconName = iconName;
        this.toggleIconName = toggleIconName;
        this.tooltip = tooltip;
        this.tooltipToggled = tooltipToggled;
    }

    disable() {
        this.disabled = true;
    }

    enable() {
        this.disabled = false;
    }

    /**
     * @abstract
     */
    onClicked() {}

    /**
     * In case if needed, redefine .extra-button-toggled css in derived class
     */
    setToggled(value: boolean) {
        this.toggled = value;
    }

    isToggled() {
        return this.toggled;
    }

    playToggleAnimation(duration: number = 800) {
        this.setToggled(true);
        this.disable();
        setTimeout(() => {
            this.enable();
            this.setToggled(false);
        }, duration);
    }

    render() {
        if (!this.visible) return nothing;

        return html`
              <sl-tooltip content="${this.toggled ? this.tooltipToggled : this.tooltip}">
                <sl-icon-button id="extra-button"
                                name="${this.toggled ? this.toggleIconName : this.iconName}"
                                class="${this.toggled ? "extra-button-toggled" : "extra-button"}"
                                ?disabled="${this.disabled}"
                                @click="${this.onClicked}"></sl-icon-button>
              </sl-tooltip>
              <div class="extra-button-overlay"></div>
        `;
    }

    static styles: CSSResultGroup = [
        css`
          sl-icon-button.extra-button::part(base),
          sl-icon-button.extra-button-toggled::part(base) {
            border: none;
            background: transparent;
            color: var(--sl-color-neutral-500);
            padding: 0;
          }

          sl-icon-button.extra-button::part(base):hover {
            color: var(--sl-color-cyan-800);
          }

          sl-icon-button.extra-button-toggled::part(base) {
            color: var(--sl-color-cyan-600);
          }

          .extra-button-toggled {
            animation: toggled ease-in-out 0.8s;
            animation-iteration-count: 1;
          }

          @keyframes toggled {
            0% {
              transform: scale(1);
            }
            50% {
              transform: scale(0.8);
            }
            100% {
              transform: scale(1);
            }
          }
        `,
    ];
}
